// src/components/pages/admin-dash/CacheAdmin.jsx
import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Badge from "../../ui/Badge";
import Spinner from "../../ui/Spinner";
import ConfirmDialog from "../../ui/ConfirmDialog";

/**
 * Admin view for the drawings cache (CacheAdminResource, /admin/cache/drawings).
 * Backend was ready — status/enable/disable/clear — but had no UI
 * (1-ADMIN_ROADMAP.md item 2.2).
 */
export default function CacheAdmin({ onShowToast }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/admin/cache/drawings");
      setStatus(res.data);
    } catch {
      onShowToast?.("Couldn't load cache status.", "error");
    } finally {
      setLoading(false);
    }
  }, [onShowToast]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const toggleEnabled = async () => {
    setToggling(true);
    try {
      const path = status?.enabled ? "disable" : "enable";
      const res = await axiosInstance.post(`/admin/cache/drawings/${path}`);
      setStatus(res.data);
      onShowToast?.(`Drawings cache ${status?.enabled ? "disabled" : "enabled"}.`, "success");
    } catch {
      onShowToast?.("Couldn't update cache state.", "error");
    } finally {
      setToggling(false);
    }
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      const res = await axiosInstance.delete("/admin/cache/drawings");
      setStatus(res.data);
      onShowToast?.("Drawings cache cleared.", "success");
      setConfirmOpen(false);
    } catch {
      onShowToast?.("Couldn't clear the cache.", "error");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
            Drawings cache
          </h1>
          <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
            Runtime controls for the drawings cache used by number generation.
          </p>
        </div>
        <Button icon="refresh" size="sm" variant="ghost" onClick={fetchStatus} loading={loading}>Refresh
        </Button>
      </header>

      <Card>
        {loading && !status ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size={18} />
          </div>
        ) : status ? (
          <>
            <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <StatusField
                label="State"
                value={
                  status.enabled
                    ? <Badge variant="teal">Enabled</Badge>
                    : <Badge variant="neutral">Disabled</Badge>
                }
              />
              <StatusField
                label="Loaded"
                value={
                  status.loaded
                    ? <Badge variant="teal">Yes</Badge>
                    : <Badge variant="gold">Not yet</Badge>
                }
              />
              <StatusField label="Entries" value={<span className="tabular-nums">{status.size ?? "—"}</span>} />
              <StatusField
                label="Backend"
                value={
                  <span style={{ color: "var(--txt)" }}>
                    {status.backend}
                    {status.fallbackBackend && (
                      <span style={{ color: "var(--txt-3)" }}> (fallback: {status.fallbackBackend})</span>
                    )}
                  </span>
                }
              />
              <StatusField
                label="Redis reachable"
                value={
                  status.redisReachable
                    ? <Badge variant="teal">Yes</Badge>
                    : <Badge variant="red">No</Badge>
                }
              />
            </div>

            <div className="flex items-center justify-end gap-2 mt-6 pt-4" style={{ borderTop: "1px solid var(--border)" }}>
              <Button
                variant={status.enabled ? "ghost" : "gold"}
                icon={status.enabled ? "x" : "check"}
                size="sm"
                onClick={toggleEnabled}
                loading={toggling}
              >
                {status.enabled ? "Disable cache" : "Enable cache"}
              </Button>
              <Button variant="danger" icon="trash" size="sm" onClick={() => setConfirmOpen(true)}>
                Clear cache
              </Button>
            </div>
          </>
        ) : (
          <p className="text-[13px] py-4 text-center" style={{ color: "var(--txt-2)" }}>
            Couldn't load cache status.
          </p>
        )}
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        title="Clear the drawings cache?"
        message="Empties every cached entry. The next instance to start will re-run the preload from Postgres — reads will fall through to the database until then."
        confirmLabel="Clear"
        tone="danger"
        loading={clearing}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleClear}
        icon="trash"
      />
    </div>
  );
}

function StatusField({ label, value }) {
  return (
    <div>
      <div className="lg-label mb-1">{label}</div>
      <div className="text-[13.5px]" style={{ color: "var(--txt)" }}>{value ?? "—"}</div>
    </div>
  );
}
